import type { Capability } from '@taler/api-client'
import type { NavIconName } from '@taler/ui'
import { canViewContacts } from '@/features/contacts'
import { canViewDashboard } from '@/features/dashboard'
import { canViewTransactions } from '@/features/transactions'
import { canViewAccounts } from '@/features/accounts'
import { canViewLoans } from '@/features/loans'
import { canViewGarage } from '@/features/garage'
import { canViewTransfers } from '@/features/transfers'
import { canViewRelationships } from '@/features/relationships'
import { canViewReporting } from '@/features/reporting'
import { canViewTargets } from '@/features/targets'

/**
 * `places` is the journal and the records a household keeps; `insight` reads the ledger back;
 * `shared` is what crosses ledgers. The sidebar renders a heading above every group but the
 * first - which is also what tells Contacts and Relationships apart in Hungarian, where both
 * are "Kapcsolatok".
 */
export type NavGroup = 'places' | 'insight' | 'shared'

export interface NavItem {
  readonly key: string
  readonly to: { readonly name: string }
  readonly labelKey: string
  readonly icon: NavIconName
  readonly group: NavGroup
}

interface NavItemDefinition extends NavItem {
  isVisible(can: (capability: Capability) => boolean): boolean
}

/**
 * Foundation proves the scaffolding works; it is developer chrome, not a nav destination,
 * and earns a place here only once it becomes one. The invitation-accept route is reached
 * only by an emailed link, never from navigation, so it has no entry here either.
 *
 * A nav item is a place you return to (AGENTS.md, "The membership rule"). Exports and Members
 * are a tool over another screen's data and a settings screen respectively - each lives as a
 * page action or account-menu entry instead, at @/pages/Transactions.vue and
 * @/app/shell/AccountMenu.vue. A cross-ledger transfer or expense is composed inline from the
 * normal entry forms, so it needs no page action of its own. The three reporting screens are
 * one destination, one server aggregate behind one capability, gated once here and reached at
 * its first tab. The dashboard is the home screen, so it is its own first item rather than a
 * tab of Reports.
 *
 * Targets and Forecast are the same shape for the same reason, just not a shared aggregate:
 * `canViewTargets` and `canViewForecast` are both exactly `RECORDS_MANAGE`, so folding Forecast
 * into Reports would have mismatched in both directions (a RECORDS_MANAGE-only member would
 * lose it, an AGGREGATES_VIEW-only one would wrongly gain it). `PlanningTabs.vue`
 * (`@taler/web`'s `features/targets`) is the tab strip; this entry's `to` still points at the
 * first tab, `targets`, the same convention as `reporting`'s `to: { name: 'reporting-overview' }`.
 */
const NAV_ITEMS: readonly NavItemDefinition[] = [
  {
    key: 'dashboard',
    to: { name: 'dashboard' },
    labelKey: 'dashboard.title',
    icon: 'dashboard',
    group: 'places',
    isVisible: (can) => canViewDashboard(can),
  },
  {
    key: 'transactions',
    to: { name: 'transactions' },
    labelKey: 'transactions.title',
    icon: 'transactions',
    group: 'places',
    isVisible: (can) => canViewTransactions(can),
  },
  {
    key: 'accounts',
    to: { name: 'accounts' },
    labelKey: 'accounts.title',
    icon: 'accounts',
    group: 'places',
    isVisible: (can) => canViewAccounts(can),
  },
  {
    key: 'contacts',
    to: { name: 'contacts' },
    labelKey: 'contacts.title',
    icon: 'contacts',
    group: 'places',
    isVisible: (can) => canViewContacts(can),
  },
  {
    key: 'loans',
    to: { name: 'loans' },
    labelKey: 'loans.title',
    icon: 'loans',
    group: 'places',
    isVisible: (can) => canViewLoans(can),
  },
  {
    key: 'garage',
    to: { name: 'garage' },
    labelKey: 'garage.title',
    icon: 'garage',
    group: 'places',
    isVisible: (can) => canViewGarage(can),
  },
  {
    key: 'reporting',
    to: { name: 'reporting-overview' },
    labelKey: 'reporting.title',
    icon: 'reports',
    group: 'insight',
    isVisible: (can) => canViewReporting(can),
  },
  {
    key: 'targets',
    to: { name: 'targets' },
    labelKey: 'planning.title',
    icon: 'planning',
    group: 'insight',
    isVisible: (can) => canViewTargets(can),
  },
  {
    key: 'relationships',
    to: { name: 'relationships' },
    labelKey: 'relationships.title',
    icon: 'relationships',
    group: 'shared',
    isVisible: (can) => canViewRelationships(can),
  },
  {
    key: 'transfers-inbox',
    to: { name: 'transfers-inbox' },
    labelKey: 'transfers.inboxTitle',
    icon: 'inbox',
    group: 'shared',
    isVisible: (can) => canViewTransfers(can),
  },
]

export function visibleNavItems(can: (capability: Capability) => boolean): readonly NavItem[] {
  return NAV_ITEMS.filter((item) => item.isVisible(can))
}
